package com.dotdrives.web.pages.nextsteps

import com.dotdrives.controller.NewReminderController
import com.dotdrives.controller.NewReminderUiState
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.design.DotButtonVariant
import com.dotdrives.model.OrgMember
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotButton
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.textarea
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text

external interface NewReminderDialogProps : Props {
    var controller: NewReminderController
    var state: NewReminderUiState
    var members: List<OrgMember>
    var currentUserId: String
    var currentUserName: String
    var now: kotlin.time.Instant
    var onCancel: () -> Unit
}

private enum class NewReminderPopover { None, Recurrence, Assignee }

/** "New reminder" dialog (`portal-next-steps.dc.html`). */
val NewReminderDialog = FC<NewReminderDialogProps> { props ->
    com.dotdrives.web.ui.useDialogFocus(true, "new-reminder-dialog", props.onCancel)
    val state = props.state
    val controller = props.controller
    val (popover, setPopover) = useState(NewReminderPopover.None)

    div {
        className = ClassName("ns-dialog-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onCancel() }

        div {
            className = ClassName("ns-dialog")
            id = web.dom.ElementId("new-reminder-dialog")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "New reminder"
            onClick = { it.stopPropagation() }

            div { className = ClassName("ns-dialog-title"); +"New reminder" }

            state.error?.let { error ->
                DotBanner { message = error; className = "ns-inline-banner" }
            }

            textarea {
                className = ClassName("ns-composer")
                value = state.description
                placeholder = "What needs to happen?"
                ariaLabel = "Reminder description"
                onChange = { controller.setDescription(it.currentTarget.value) }
            }

            div {
                className = ClassName("ns-chip-row")
                span {
                    className = ClassName("ns-chip-select ns-chip-select-active")
                    input {
                        // Native date input rendered borderless inside the chip so the whole
                        // control still reads as the design's "📅 Thursday ▾" pill.
                        type = "date".unsafeCast<InputType>()
                        ariaLabel = "Due date"
                        onChange = { event ->
                            val value = event.currentTarget.value
                            if (value.isNotBlank()) controller.setDueAt(kotlin.time.Instant.parse("${value}T09:00:00Z"))
                        }
                    }
                }
                react.dom.html.ReactHTML.button {
                    type = ButtonType.buttonType
                    className = ClassName("ns-chip-select")
                    onClick = { setPopover(if (popover == NewReminderPopover.Recurrence) NewReminderPopover.None else NewReminderPopover.Recurrence) }
                    +"↻ ${recurrenceLabel(state.recurrence) ?: "Doesn't repeat"} ▾"
                }
                react.dom.html.ReactHTML.button {
                    type = ButtonType.buttonType
                    className = ClassName("ns-chip-select")
                    onClick = { setPopover(if (popover == NewReminderPopover.Assignee) NewReminderPopover.None else NewReminderPopover.Assignee) }
                    +"👤 ${assigneeChipLabel(state.assignedToUserId, props.currentUserId, props.currentUserName, props.members)} ▾"
                }
            }
            if (popover == NewReminderPopover.Recurrence) {
                RecurrencePopover(current = state.recurrence) { value ->
                    controller.setRecurrence(value)
                    setPopover(NewReminderPopover.None)
                }
            }
            if (popover == NewReminderPopover.Assignee) {
                ReassignPopover {
                    members = listOf(OrgMember(props.currentUserId, "Me", "member")) + props.members.filter { it.userId != props.currentUserId }
                    currentUserId = state.assignedToUserId ?: props.currentUserId
                    onPick = { userId, _ -> controller.setAssignedToUserId(userId); setPopover(NewReminderPopover.None) }
                }
            }

            div { className = ClassName("ns-subject-label"); +"Linked to" }
            if (state.selectedSubject == null) {
                input {
                    className = ClassName("ns-subject-search")
                    type = InputType.text
                    value = state.subjectQuery
                    placeholder = "Search givers, drives…"
                    ariaLabel = "Search givers or drives"
                    onChange = { controller.searchSubject(it.currentTarget.value) }
                }
                if (state.subjectResults.isNotEmpty()) {
                    div {
                        className = ClassName("ns-subject-results")
                        state.subjectResults.forEach { option ->
                            val (bg, fg) = DotAvatarPalette.tintFor(option.subjectId)
                            val avatarStyle = js("({})")
                            avatarStyle.backgroundColor = bg
                            avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(fg)
                            react.dom.html.ReactHTML.button {
                                key = "${option.subjectType}-${option.subjectId}".unsafeCast<Key>()
                                type = ButtonType.buttonType
                                className = ClassName("ns-subject-result")
                                onClick = { controller.selectSubject(option) }
                                div { className = ClassName("ns-subject-avatar"); style = avatarStyle; +com.dotdrives.web.pages.drives.driveInitials(option.label) }
                                div {
                                    className = ClassName("ns-subject-copy")
                                    div { className = ClassName("ns-subject-name"); +option.label }
                                    div { className = ClassName("ns-subject-context"); +option.context }
                                }
                            }
                        }
                    }
                }
            } else {
                val subject = state.selectedSubject!!
                val (bg, fg) = DotAvatarPalette.tintFor(subject.subjectId)
                val avatarStyle = js("({})")
                avatarStyle.backgroundColor = bg
                avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(fg)
                div {
                    className = ClassName("ns-subject-selected")
                    div { className = ClassName("ns-subject-avatar"); style = avatarStyle; +com.dotdrives.web.pages.drives.driveInitials(subject.label) }
                    div {
                        className = ClassName("ns-subject-copy")
                        div { className = ClassName("ns-subject-name"); +subject.label }
                        div { className = ClassName("ns-subject-context"); +subject.context }
                    }
                    react.dom.html.ReactHTML.button {
                        type = ButtonType.buttonType
                        className = ClassName("ns-row-more")
                        ariaLabel = "Change linked record"
                        onClick = { controller.searchSubject("") }
                        +"✕"
                    }
                }
            }

            div {
                className = ClassName("ns-email-row")
                react.dom.html.ReactHTML.button {
                    type = ButtonType.buttonType
                    className = ClassName("ns-toggle" + if (state.alsoEmailMe) " ns-toggle-on" else "")
                    ariaLabel = "Email the assignee when due"
                    ariaPressed = state.alsoEmailMe.unsafeCast<react.dom.aria.AriaPressed>()
                    onClick = { controller.setAlsoEmailMe(!state.alsoEmailMe) }
                    span { className = ClassName("ns-toggle-knob") }
                }
                span { className = ClassName("ns-email-label"); +"Email the assignee when due" }
            }

            div {
                className = ClassName("ns-dialog-actions")
                DotButton {
                    text = "Cancel"
                    variant = DotButtonVariant.Secondary
                    onClick = props.onCancel
                    enabled = !state.saving
                }
                DotButton {
                    text = "Create reminder"
                    onClick = controller::save
                    loading = state.saving
                }
            }
        }
    }
}

private fun react.ChildrenBuilder.RecurrencePopover(current: String?, onPick: (String?) -> Unit) {
    div {
        className = ClassName("ns-popover")
        listOf(null, "weekly", "monthly", "quarterly").forEach { value ->
            react.dom.html.ReactHTML.button {
                key = (value ?: "none").unsafeCast<Key>()
                type = ButtonType.buttonType
                className = ClassName("ns-popover-item" + if (value == current) " ns-popover-item-active" else "")
                onClick = { onPick(value) }
                +(value?.replaceFirstChar(Char::uppercase) ?: "Doesn't repeat")
            }
        }
    }
}

// driveInitials already provides these two-letter avatar initials -- reused verbatim
// (com.dotdrives.web.pages.drives.driveInitials) rather than hand-rolled again here.

private fun assigneeChipLabel(
    assignedToUserId: String?,
    currentUserId: String,
    currentUserName: String,
    members: List<OrgMember>,
): String = when {
    assignedToUserId == null || assignedToUserId == currentUserId -> "Me"
    else -> members.firstOrNull { it.userId == assignedToUserId }?.displayName ?: currentUserName
}
